"use client";

import { forwardRef, type MouseEventHandler, type ReactNode } from "react";

import { createPortal } from "react-dom";

import * as S from "./EllipsisTooltip.style";

export type EllipsisTooltipAnchorRect = {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
};

export type EllipsisTooltipProps = {
  children: ReactNode;
  tooltip?: ReactNode | null;
  tooltipMaxWidth?: string;
  sameWidthAsText?: boolean;
  isTooltipTextBold?: boolean;
  showTooltip?: boolean;
  tooltipPosition?: "left" | "right";
  tooltipVerticalPosition?: "top" | "bottom";
  tooltipAnchorRect?: EllipsisTooltipAnchorRect | null;
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
};

const EllipsisTooltip = forwardRef<HTMLDivElement, EllipsisTooltipProps>(
  (
    {
      children,
      tooltip,
      tooltipMaxWidth = "40rem",
      sameWidthAsText = false,
      isTooltipTextBold = false,
      showTooltip = false,
      tooltipPosition = "left",
      tooltipVerticalPosition = "bottom",
      tooltipAnchorRect = null,
      onMouseEnter,
      onMouseLeave,
    },
    ref,
  ) => {
    const tooltipElement = (
      <S.TooltipContainer
        $position={tooltipPosition}
        $verticalPosition={tooltipVerticalPosition}
        $anchorRect={tooltipAnchorRect}
        $maxWidth={tooltipMaxWidth}
        $sameWidthAsText={sameWidthAsText}
      >
        <S.TooltipBase>
          <S.TooltipClamp $isBold={isTooltipTextBold}>{tooltip}</S.TooltipClamp>
        </S.TooltipBase>
      </S.TooltipContainer>
    );

    return (
      <S.Wrapper
        ref={ref}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {children}
        {showTooltip
          ? tooltipAnchorRect
            ? createPortal(tooltipElement, document.body)
            : tooltipElement
          : null}
      </S.Wrapper>
    );
  },
);

EllipsisTooltip.displayName = "EllipsisTooltip";

export default EllipsisTooltip;
